import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { logActivity, logCrud } from "@/services/activities";

interface PaymentMethodWithSlug {
  id: string;
  nome: string | null;
  slug: string | null;
}

interface BankAccountMethodRelation {
  payment_method_id: string;
  payment_methods: PaymentMethodWithSlug | null;
}

interface BankAccountWithMethods {
  id: string;
  nome: string;
  bank_accounts_methods?: BankAccountMethodRelation[];
}

interface PaymentConfigForm {
  daysToReceive: string;
  feePercent: string;
}

type MethodType = "credito" | "debito";

interface AccountMethodRow {
  key: string;
  accountId: string;
  accountName: string;
  paymentMethodId: string;
  methodDisplayName: string;
  methodType: MethodType;
}

interface PaymentSettlementConfig {
  id: string;
  tenant_id: string;
  bank_account_id: string;
  payment_method_id: string;
  method_type: MethodType;
  days_to_receive: number | null;
  fee_percent: number | null;
}

const normalizeText = (value?: string | null) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[^\p{ASCII}]/gu, "")
    .toLowerCase();

const resolveMethodType = (method?: PaymentMethodWithSlug | null): MethodType | null => {
  if (!method) return null;

  const normalizedSlug = normalizeText(method.slug);
  const normalizedName = normalizeText(method.nome);

  if (normalizedSlug.includes("credito") || normalizedName.includes("credito")) {
    return "credito";
  }

  if (normalizedSlug.includes("debito") || normalizedName.includes("debito")) {
    return "debito";
  }

  return null;
};

export function PaymentSettlementSettings() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [configs, setConfigs] = useState<Record<string, PaymentConfigForm>>({});

  const {
    data: bankAccounts = [],
    isLoading: isLoadingAccounts,
    error: bankAccountsError,
  } = useQuery({
    queryKey: ["bank-accounts-with-methods", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [] as BankAccountWithMethods[];

      const { data, error: queryError } = await supabase
        .from("bank_accounts")
        .select(
          `
            id,
            nome,
            bank_accounts_methods (
              payment_method_id,
              payment_methods:payment_method_id (
                id,
                nome,
                slug
              )
            )
          `
        )
        .eq("tenant_id", tenantId)
        .eq("status", "ativo")
        .order("nome");

      if (queryError) throw queryError;

      return (data ?? []) as BankAccountWithMethods[];
    },
  });

  const {
    data: settlementConfigs = [],
    isLoading: isLoadingConfigs,
    error: settlementConfigsError,
  } = useQuery({
    queryKey: ["payment-settlement-configs", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [] as PaymentSettlementConfig[];

      const { data, error: queryError } = await supabase
        .from("payment_settlement_configs")
        .select("*")
        .eq("tenant_id", tenantId);

      if (queryError) throw queryError;

      return (data ?? []) as PaymentSettlementConfig[];
    },
  });

  useEffect(() => {
    if (bankAccountsError) {
      toast.error("Não foi possível carregar as contas para configurar os prazos.");
    }
  }, [bankAccountsError]);

  useEffect(() => {
    if (settlementConfigsError) {
      toast.error("Não foi possível carregar as configurações salvas.");
    }
  }, [settlementConfigsError]);

  const creditAndDebitRelations = useMemo<AccountMethodRow[]>(() => {
    return bankAccounts.flatMap<AccountMethodRow>((account) => {
      if (!account.bank_accounts_methods) return [];

      return account.bank_accounts_methods
        .map((relation) => {
          const methodType = resolveMethodType(relation.payment_methods);
          if (!methodType || !relation.payment_methods) return null;

          return {
            key: `${account.id}-${relation.payment_method_id}`,
            accountId: account.id,
            accountName: account.nome,
            paymentMethodId: relation.payment_method_id,
            methodDisplayName:
              methodType === "credito"
                ? "Cartão de Crédito"
                : "Cartão de Débito",
            methodType,
          } satisfies AccountMethodRow;
        })
        .filter((value): value is AccountMethodRow => Boolean(value));
    });
  }, [bankAccounts]);

  const settlementConfigsMap = useMemo(() => {
    const map = new Map<string, PaymentSettlementConfig>();
    settlementConfigs.forEach((config) => {
      map.set(`${config.bank_account_id}-${config.payment_method_id}`, config);
    });
    return map;
  }, [settlementConfigs]);

  useEffect(() => {
    setConfigs((prev) => {
      const comboKeys = new Set(creditAndDebitRelations.map((item) => item.key));
      const next: Record<string, PaymentConfigForm> = {};
      let changed = false;

      creditAndDebitRelations.forEach((item) => {
        const existing = settlementConfigsMap.get(`${item.accountId}-${item.paymentMethodId}`);
        const nextValue: PaymentConfigForm = {
          daysToReceive: existing?.days_to_receive !== null && existing?.days_to_receive !== undefined
            ? existing.days_to_receive.toString()
            : "",
          feePercent: existing?.fee_percent !== null && existing?.fee_percent !== undefined
            ? existing.fee_percent.toString()
            : "",
        };

        next[item.key] = nextValue;

        const prevValue = prev[item.key];
        if (!prevValue || prevValue.daysToReceive !== nextValue.daysToReceive || prevValue.feePercent !== nextValue.feePercent) {
          changed = true;
        }
      });

      if (Object.keys(prev).length !== Object.keys(next).length) {
        changed = true;
      }

      if (!changed) {
        return prev;
      }

      return next;
    });
  }, [creditAndDebitRelations, settlementConfigsMap]);

  const updateConfig = (key: string, field: keyof PaymentConfigForm, value: string) => {
    setConfigs((prev) => ({
      ...prev,
      [key]: {
        daysToReceive: prev[key]?.daysToReceive ?? "",
        feePercent: prev[key]?.feePercent ?? "",
        [field]: value,
      },
    }));
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!tenantId) throw new Error("Tenant não encontrado");

      const payload = creditAndDebitRelations.map((item) => {
        const form = configs[item.key] ?? { daysToReceive: "", feePercent: "" };

        const parseDays = (): number | null => {
          const raw = form.daysToReceive.trim();
          if (!raw) return null;
          const value = Math.max(0, Math.round(Number(raw)));
          if (!Number.isFinite(value)) {
            throw new Error("Informe apenas números válidos para os prazos.");
          }
          return value;
        };

        const parseFee = (): number | null => {
          const raw = form.feePercent.trim();
          if (!raw) return null;
          const value = Math.max(0, Number(raw));
          if (!Number.isFinite(value)) {
            throw new Error("Informe apenas números válidos para as taxas.");
          }
          return Number(value.toFixed(2));
        };

        return {
          tenant_id: tenantId,
          bank_account_id: item.accountId,
          payment_method_id: item.paymentMethodId,
          method_type: item.methodType,
          days_to_receive: parseDays(),
          fee_percent: parseFee(),
        };
      });

      if (payload.length > 0) {
        const { error: upsertError } = await supabase
          .from("payment_settlement_configs")
          .upsert(payload, {
            onConflict: "tenant_id,bank_account_id,payment_method_id",
          });

        if (upsertError) throw upsertError;
      }

      const keysToKeep = new Set(
        payload.map((item) => `${item.bank_account_id}-${item.payment_method_id}`)
      );

      const obsolete = settlementConfigs.filter(
        (config) =>
          !keysToKeep.has(`${config.bank_account_id}-${config.payment_method_id}`)
      );

      if (obsolete.length > 0) {
        const { error: deleteError } = await supabase
          .from("payment_settlement_configs")
          .delete()
          .in(
            "id",
            obsolete.map((config) => config.id)
          );

        if (deleteError) throw deleteError;
      }

      if (payload.length === 0 && settlementConfigs.length > 0) {
        const { error: deleteAllError } = await supabase
          .from("payment_settlement_configs")
          .delete()
          .eq("tenant_id", tenantId);

        if (deleteAllError) throw deleteAllError;
      }
    },
    onSuccess: async () => {
      toast.success("Configurações salvas com sucesso!");
      await queryClient.invalidateQueries({
        queryKey: ["payment-settlement-configs", tenantId],
      });
      const meta = { count: creditAndDebitRelations.length } as any;
      void logActivity({ title: "Configurações de recebimento", description: "Ajustes salvos", categorySlug: "category:payments", entitySlug: "entity:payment_settlement_configs", operationSlug: "operation:update", screenSlug: "configuracoes/contas-bancarias", metadata: meta });
    },
    onError: (mutationError: any) => {
      const message = mutationError?.message ?? "Erro ao salvar as configurações.";
      toast.error(message);
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Configurações de Recebimento</CardTitle>
          <CardDescription>
            Defina prazos de repasse por conta para cartões de crédito e débito.
          </CardDescription>
        </div>
        <Button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={
            creditAndDebitRelations.length === 0 ||
            isLoadingAccounts ||
            isLoadingConfigs ||
            saveMutation.isPending
          }
        >
          {saveMutation.isPending ? "Salvando..." : "Salvar ajustes"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoadingAccounts || isLoadingConfigs ? (
          <p className="text-sm text-muted-foreground">Carregando contas e métodos...</p>
        ) : creditAndDebitRelations.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Cadastre contas com os métodos Cartão de Crédito e/ou Cartão de Débito para configurar prazos.
          </p>
        ) : (
          <div className="border rounded-lg divide-y">
            {creditAndDebitRelations.map((item) => {
              const config = configs[item.key] ?? { daysToReceive: "", feePercent: "" };

              return (
                <div
                  key={item.key}
                  className="grid gap-4 px-4 py-4 md:grid-cols-[1.5fr_1fr]"
                >
                  <div>
                    <p className="font-medium text-sm md:text-base">{item.accountName}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <Badge
                        variant="outline"
                        className="bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-200/60 dark:bg-sky-900/60 dark:text-sky-100 dark:border-sky-700 dark:hover:bg-sky-900"
                      >
                        {item.methodDisplayName}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor={`days-${item.key}`}>Prazo (dias)</Label>
                    <Input
                      id={`days-${item.key}`}
                      type="number"
                      min={0}
                      value={config.daysToReceive}
                      placeholder="Ex: 30"
                      onChange={(event) => updateConfig(item.key, "daysToReceive", event.target.value)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
